import React from 'react';
import { TextInput, StyleSheet, Platform, View, Pressable, TextStyle, ViewStyle, StyleProp } from 'react-native';
import { APP_CONSTANTS } from '@/constants/residents';
import { strings } from '@/constants/residents/strings';
import { SVG_COMPONENTS } from '@/assets/admin';
import { useThemedStyles, useThemeColors } from '@/hooks/shared/useThemeColors';
import type { ThemeContextValue } from '@/hooks/shared/useThemeColors';

type Props = {
  value: string;
  onChangeText: (text: string) => void;
  placeholder?: string;
  showPassword: boolean;
  toggleShowPassword: () => void;
  inputRef?: React.RefObject<TextInput | null>;
  onSubmitEditing?: () => void;
  style?: StyleProp<TextStyle | ViewStyle>;
};

export const PasswordInput: React.FC<Props> = ({
  value,
  onChangeText,
  placeholder = strings.login.field.passwordPlaceholder,
  showPassword,
  toggleShowPassword,
  inputRef,
  onSubmitEditing,
  style,
}) => {
  const styles = useThemedStyles(makeStyles);
  const { colors } = useThemeColors();

  return (
    <View style={styles.container}>
      <TextInput
        ref={inputRef}
        style={[styles.input, styles.passwordInput, style as any]}
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        // See EmailInput for the placeholder-token reasoning.
        placeholderTextColor={colors.text.muted}
        secureTextEntry={!showPassword}
        autoCapitalize="none"
        textContentType="password"
        onSubmitEditing={onSubmitEditing}
      />
      <Pressable style={styles.eyeButton} onPress={toggleShowPassword}>
        {showPassword ? (
          <SVG_COMPONENTS.EYE_OPEN width={24} height={24} color={colors.text.muted} />
        ) : (
          <SVG_COMPONENTS.EYE_CLOSED width={24} height={24} color={colors.text.muted} />
        )}
      </Pressable>
    </View>
  );
};

// Mapping mirrors components/auth/PasswordInput, the already-themed sibling.
const makeStyles = ({ colors, semantic }: ThemeContextValue) => StyleSheet.create({
  container: {
    position: 'relative',
    width: '100%',
  },
  input: {
    height: APP_CONSTANTS.TEXT_FIELD_HEIGHT,
    borderWidth: 1,
    borderColor: semantic.border.secondary,
    borderRadius: APP_CONSTANTS.BUTTON_CORNER_RADIUS,
    paddingHorizontal: 16,
    fontSize: APP_CONSTANTS.INPUT_FONT_SIZE,
    color: colors.text.primary,
    backgroundColor: semantic.background.input,
    fontFamily: Platform.select({
      ios: 'Avenir-Book',
      android: 'sans-serif',
      default: 'System',
    }),
  },
  passwordInput: {
    paddingRight: 50,
    width: '100%',
  },
  eyeButton: {
    position: 'absolute',
    right: 16,
    top: 0,
    height: APP_CONSTANTS.TEXT_FIELD_HEIGHT,
    justifyContent: 'center',
  },
});


